import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon, IconNames, Dialog, DialogContentType, DialogFooterType } from '@dotcom/crown-ui';
import useAddressSelection from '../../hooks/use-address-selection';
import { getCurrentRegion } from '../../regional-config/get-regional-config';
import { Contact } from '../address-book';
import { DeleteAddressFromProjectConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import contactStyles from '../contact-card/contact-card.module.scss';
import { Contact as ContactCard } from '../contact/contact';
import { DetailRecipients } from './fragments/detail-recipients';
import { OrderQuantity } from './fragments/order-quantity';
import { useDeleteAddress } from './hooks/use-delete-address';
import { useOrderQuantitySummary } from './hooks/use-order-quantity-summary';
import { RecipientAddressesProps, RecipientAddressesVariant } from './recipient-addresses-dialog-types';
import styles from './recipient-addresses-dialog.module.scss';
import { handleRecipientDialogCloseAndNavigation } from './utils/handle-recipient-dialog-close-and-navigation';

export const RecipientAddressesDialog = React.memo(
  ({
    isOpen,
    setRecipientAddressesDialogOpen,
    variant,
    onActionButtonClick,
    onSecondaryActionButtonClick,
    setOpenEditDialog,
    resetRecipientAddressForm,
  }: RecipientAddressesProps) => {
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const { contacts, sender } = useAddressSelection();
    const { t } = useTranslation();
    const region = useMemo(() => getCurrentRegion(), []);
    const returnAndRecipientAddresses = contacts.length;
    const { quantity, blankEnvelopes } = useOrderQuantitySummary({ returnAndRecipientAddresses });
    const { deleteAddress } = useDeleteAddress();
    const navigate = useNavigate();
    const { search } = useLocation();

    const onEditButtonClick: () => void = useCallback(() => {
      handleRecipientDialogCloseAndNavigation(search, navigate, setRecipientAddressesDialogOpen);
    }, [search, navigate, setRecipientAddressesDialogOpen]);

    const onEditContact: (contact: Contact) => void = useCallback(
      (contact) => {
        // set the values of the contact to the form
        resetRecipientAddressForm({
          address_id: contact.contact_id,
          first_name: contact.first_name,
          last_name: contact.last_name,
          address_line_1: contact.address_line_1,
          address_line_2: contact.address_line_2,
          city: contact.city,
          state_code: contact.state_code,
          zip: contact.zip,
          country_code: contact.country_code,
        });

        setOpenEditDialog(true);
        setRecipientAddressesDialogOpen(false);
      },
      [resetRecipientAddressForm, setOpenEditDialog, setRecipientAddressesDialogOpen],
    );

    // triggers when the user clicks on the delete action button
    const handleDeleteAction: (contactId: string) => Promise<void> = useCallback(async (contactId: string) => {
      setIdToDelete(contactId);
      setIsConfirmationDialogOpen(true);
    }, []);

    // trrigers when the user confirms the delete action
    const handleDeleteConfirmation: () => Promise<void> = useCallback(async () => {
      if (idToDelete) {
        await deleteAddress(idToDelete);
        setIsConfirmationDialogOpen(false);
      }
    }, [deleteAddress, idToDelete]);

    // TODO: Remove this when dialog component is migrated to CUI
    const dialogAccentIcon: any = IconNames.AddressBookBold;

    return isOpen ? (
      <>
        <Dialog
          testId={'recipient-addresses-dialog'}
          isOpen={isOpen}
          onClose={() => setRecipientAddressesDialogOpen(false)}
          accentIcon={dialogAccentIcon}
          title={String(t('recipientAddresses.header'))}
          type={DialogContentType.FeatureModal}
          footerType={
            variant === RecipientAddressesVariant.Address ? DialogFooterType.WithShadow : DialogFooterType.SingleButton
          }
          locale={region}
          dialogClassName={`${styles.recipientAddressesDialog} ${
            variant === RecipientAddressesVariant.Address ? styles.recipientAddressesWrapper : ''
          }`}
          secondaryActionButton={
            variant === RecipientAddressesVariant.Address ? (
              <Button testId="secondary-action-button" onClick={onSecondaryActionButtonClick} variant="secondary">
                {t('recipientAddresses.address.secondaryActionButton')}
              </Button>
            ) : (
              ''
            )
          }
          actionButton={
            <Button
              testId="action-button"
              onClick={onActionButtonClick}
              variant={variant === RecipientAddressesVariant.Address ? 'primary' : 'secondary'}
            >
              {variant === RecipientAddressesVariant.Address
                ? t('recipientAddresses.address.actionButton')
                : t('recipientAddresses.preview.actionButton')}
            </Button>
          }
        >
          <div className={styles.recipientAddresses}>
            <OrderQuantity quantity={quantity} />
            {sender && (
              <div
                className={classnames(
                  contactStyles['contact-card'],
                  styles.contact,
                  styles['recipient-address-contact'],
                )}
              >
                <Icon name={IconNames.AddressBookBold} color={'dark-gray'} />
                <ContactCard
                  topLabel="Return address"
                  contact={sender}
                  onEdit={onEditContact}
                  testId="sender-card"
                  hasTopActionButtons
                />
              </div>
            )}
            <DetailRecipients
              iconName={IconNames.Mail}
              iconTestId="icon-Mail"
              description={t('recipientAddresses.detailRecipients.blankEnvelopes')}
              amount={blankEnvelopes}
              onEditButtonClick={onEditButtonClick}
              shouldShowBorderBottom
            />
            <DetailRecipients
              iconName={IconNames.Mail}
              iconTestId="icon-Mail"
              description={t('recipientAddresses.detailRecipients.returnAndRecipientAddresses')}
              amount={returnAndRecipientAddresses}
            />
            {contacts && contacts.length > 0 ? (
              contacts.map((contact, index) => (
                <div
                  className={classnames(contactStyles['contact-card'], styles.contact, {
                    [styles['last-contact']]: index === contacts.length - 1,
                  })}
                  key={index}
                >
                  <div className={styles.emptyIconContainer} />
                  <ContactCard
                    contact={contact}
                    onDelete={handleDeleteAction}
                    onEdit={onEditContact}
                    testId="recipient-card"
                    hasTopActionButtons
                  />
                </div>
              ))
            ) : (
              <></>
            )}
          </div>
        </Dialog>
        {isConfirmationDialogOpen && (
          <DeleteAddressFromProjectConfirmationDialog
            isOpen={isConfirmationDialogOpen}
            onClose={() => setIsConfirmationDialogOpen(false)}
            onConfirm={handleDeleteConfirmation}
          />
        )}
      </>
    ) : (
      <></>
    );
  },
);
